import {
  ErrorMessages,
  InputError,
  InputValidator,
  NestedInputError,
  NestedInputValidation,
  ValidatorArray,
} from '@csp/csp-common-form';
import { isEmpty } from 'lodash';
import { FieldErrors, FieldValues, Resolver, ResolverResult } from 'react-hook-form';

export type ValidationSchema<TFieldValues extends FieldValues = FieldValues, TContext extends object = object> = {
  [K in keyof TFieldValues]: (
    values: TFieldValues,
    context?: TContext,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => InputValidator<any>[] | NestedInputValidation<any>;
};

// exported for test purposes
export const validateValue = (
  data: string | number,
  validators: ValidatorArray,
  errorMessages: ErrorMessages,
): InputError | undefined => {
  for (const validator of validators) {
    // casting validator so it can handle both string and number as input
    const errorOrNull = (
      validator as (val: string | number, errorMessages: ErrorMessages | string) => InputError | null
    )(data, errorMessages);
    if (errorOrNull != null) {
      return errorOrNull;
    }
  }
  return undefined;
};

/**
 * Creates a react-hook-form resolver that is compatible with our
 * input validators. See react-hook-form docs for useForm, parameter "resolver".
 *
 * The gist of it is that react-hook-form passes the form values to our
 * custom resolver according to the "mode" used, and we return the validation
 * results (errors). This way, we can keep the validation logic on form level,
 * and do not have to pass the validators down to our inputs.
 */
export const validationResolver =
  <TFieldValues extends FieldValues, TContext extends object = object>(
    schema: ValidationSchema<TFieldValues, TContext>,
    errorMessages: ErrorMessages,
  ): Resolver<TFieldValues, TContext> =>
  (values, context): ResolverResult<TFieldValues> => {
    const errors = Object.keys(schema).reduce((prev, cur: keyof TFieldValues) => {
      const validators = schema[cur](values as TFieldValues, context);
      const value = (values as TFieldValues)[cur];

      if ('isNestedDataValidator' in validators) {
        const nestedErrors: NestedInputError[] = value.map((nestedItem: (typeof value)[string]) =>
          validators.inputValidators.reduce((errorsForItem, { key, validators }) => {
            const error = validateValue(nestedItem[key], validators, errorMessages);
            const result = error ? { [key]: { type: error.type.toString(), message: error.message } } : { [key]: null };

            return { ...errorsForItem, ...result };
          }, {}),
        );

        const hasErrors = nestedErrors.some((error: NestedInputError) => {
          const values = Object.values(error);
          return values.some(val => val !== null);
        });

        return hasErrors ? { ...prev, [cur]: nestedErrors } : prev;
      } else {
        const err = validateValue(value, validators, errorMessages);

        return err ? { ...prev, [cur]: { type: err.type.toString(), message: err.message } } : prev;
      }
    }, {} as FieldErrors<TFieldValues>);

    if (isEmpty(errors)) {
      return {
        values,
        errors: {},
      };
    } else {
      return {
        values: {},
        errors,
      };
    }
  };
